import * as React from 'react';
import { Platform, View } from 'react-native';
import Animated, { FadeIn, FadeInDown, FadeOut, LinearTransition, runOnJS, useAnimatedProps, useDerivedValue, useSharedValue, } from 'react-native-reanimated';
import { rem, Spacer } from 'design-system';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import ListEmptyView from 'design-system/ListEmptyView';
import { styles as textStyles } from 'design-system/Text';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import CreditScoreEnhancedFactorCard from '../CreditScoreEnhancedFactorCard';
import { isPositiveFactor } from '../../utils';
const strings = {
    sectionTitle: 'Historic factors breakdown',
    emptyTitle: 'No credit factors',
    emptySubTitle: "We don't have any credit factors to show for this point in time.",
};
const FADE_DURATION = 200;
const COMMIT_DEBOUNCE = 150;
const MemoFactorCard = React.memo(CreditScoreEnhancedFactorCard);
const clampIndex = (length, index) => Math.max(0, Math.min(index, length - 1));
const CreditScoreHistoryFactors = ({ dataPoints, factorsByIndex, selectedIndex, footer, }) => {
    const styles = useStyles(styleSet);
    const isAndroid = Platform.OS === 'android';
    const fallbackIndex = factorsByIndex.length > 0 ? factorsByIndex.length - 1 : 0;
    const [activeIndex, setActiveIndex] = React.useState(fallbackIndex);
    // ---- Debounced commit ----
    const timerRef = React.useRef(null);
    const commitIndex = React.useCallback((idx) => {
        if (timerRef.current)
            clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setActiveIndex(idx), isAndroid ? 400 : COMMIT_DEBOUNCE);
    }, []);
    React.useEffect(() => () => {
        if (timerRef.current)
            clearTimeout(timerRef.current);
    }, []);
    // ---- UI-thread bridge with bail-out ----
    // Skip the runOnJS hop when the index hasn't actually changed — onScroll
    // fires the worklet at ~60Hz but the index only changes at snap crossings.
    const lastDispatched = useSharedValue(fallbackIndex);
    useDerivedValue(() => {
        if (selectedIndex.value !== lastDispatched.value) {
            lastDispatched.value = selectedIndex.value;
            runOnJS(commitIndex)(selectedIndex.value);
        }
    }, [selectedIndex]);
    // ---- Section header animated label ----
    const labelTexts = React.useMemo(() => dataPoints.map((d) => d.label), [dataPoints]);
    const sectionFallbackLabel = labelTexts[fallbackIndex] ?? '';
    const labelProps = useAnimatedProps(() => ({ text: labelTexts[selectedIndex.value] ?? '' }), [selectedIndex, labelTexts]);
    const factors = factorsByIndex[clampIndex(factorsByIndex.length, activeIndex)] ?? [];
    return (<View>
      <View style={styles.sectionRow}>
        <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} defaultValue={strings.sectionTitle} style={styles.sectionText}/>
        <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={labelProps} defaultValue={sectionFallbackLabel} style={[styles.sectionText, styles.sectionLabel]}/>
      </View>
      {factors.length === 0 ? (<Animated.View key="empty" entering={FadeIn.duration(FADE_DURATION)} {...(!isAndroid ? { exiting: FadeOut.duration(FADE_DURATION) } : {})}>
          <ListEmptyView onCard happy compact title={strings.emptyTitle} subTitle={strings.emptySubTitle}/>
          <Spacer.H16 />
        </Animated.View>) : (factors.map((factor) => (<Animated.View key={factor.id} entering={FadeInDown.duration(FADE_DURATION)} {...(!isAndroid
            ? { exiting: FadeOut.duration(FADE_DURATION) }
            : {})}>
            <MemoFactorCard positiveFactor={isPositiveFactor(factor.factorColor)} title={factor.type} subTitle={factor.message}/>
          </Animated.View>)))}
      {footer ? (<Animated.View layout={LinearTransition.duration(FADE_DURATION)}>
          {footer}
        </Animated.View>) : null}
    </View>);
};
export default CreditScoreHistoryFactors;
const styleSet = createStyleSheets((colors) => ({
    sectionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginHorizontal: rem(16),
        marginTop: rem(16),
        marginBottom: rem(16),
    },
    sectionText: {
        margin: 0,
        padding: 0,
        color: colors.text.secondary,
        fontSize: textStyles.Text_16.fontSize,
        lineHeight: textStyles.Text_16.lineHeight,
        fontFamily: textStyles.Text_16.fontFamily,
    },
    sectionLabel: {
        flex: 1,
        textAlign: 'right',
    },
}));
